import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FloppyDisk, ArrowsClockwise, CheckCircle, WarningCircle, MicrosoftOutlookLogo, ArrowCounterClockwise } from "@phosphor-icons/react";
import { formatDate } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function MicrosoftTab({ settings, onSaved }) {
  const [form, setForm] = useState({
    azure_tenant_id: settings.azure_tenant_id || "",
    azure_client_id: settings.azure_client_id || "",
    azure_client_secret: "",
    shared_mailbox: settings.shared_mailbox || "Leads@barnardinc.co.za",
    allowed_domain: settings.allowed_domain || "barnardinc.co.za",
    poll_interval_minutes: settings.poll_interval_minutes || 5,
  });
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);
  const [resetting, setResetting] = useState(false);
  // When a secret is already stored we hide the input behind a "Stored" pill so
  // users don't think the value was lost. They click "Replace secret" to switch
  // into edit mode; this flag tracks that.
  const [replacingSecret, setReplacingSecret] = useState(!settings.azure_client_secret_masked);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Trim whitespace so accidental spaces don't overwrite the stored secret.
      payload.azure_client_secret = (payload.azure_client_secret || "").trim();
      if (!payload.azure_client_secret) delete payload.azure_client_secret;
      payload.poll_interval_minutes = parseInt(payload.poll_interval_minutes, 10) || 5;
      await api.patch("/settings", payload);
      toast.success("Microsoft settings saved");
      // After a successful save, clear the secret field and collapse back to the
      // "Stored" pill if one is now configured.
      setForm((f) => ({ ...f, azure_client_secret: "" }));
      setReplacingSecret(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onPollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/sync/mailbox");
      toast.success(`Polled ${data.polled} messages — ${data.new_leads} new, ${data.merged_into_existing || 0} merged`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    } finally {
      setPolling(false);
    }
  };

  const onResetIngestion = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/sync/reset-ingestion");
      toast.success(`Cleared ${data.tombstones_cleared} tombstone(s). Polling now…`);
      const { data: poll } = await api.post("/sync/mailbox");
      toast.success(`Re-ingested ${poll.new_leads} lead(s) from mailbox.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-8">
      <section className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-[#0078D4]/10 flex items-center justify-center shrink-0">
            <MicrosoftOutlookLogo size={26} weight="fill" className="text-[#0078D4]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl text-slate-900">Microsoft 365 Integration</h2>
              {settings.microsoft_configured ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-emerald-50 text-emerald-700">
                  <CheckCircle size={12} weight="fill" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-amber-50 text-amber-700">
                  <WarningCircle size={12} weight="fill" /> Not configured
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">Read emails from the shared mailbox and sync licensed users.</p>
            {settings.last_poll_at && (
              <p className="text-xs text-slate-400 mt-2">Last mailbox poll: {formatDate(settings.last_poll_at)}</p>
            )}
            {settings.last_user_sync_at && (
              <p className="text-xs text-slate-400">Last user sync: {formatDate(settings.last_user_sync_at)}</p>
            )}
          </div>
          {settings.microsoft_configured && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button data-testid="poll-now-btn" onClick={onPollNow} disabled={polling || resetting} variant="outline" size="sm" className="rounded-sm gap-2">
                <ArrowsClockwise size={14} className={polling ? "animate-spin" : ""} />
                {polling ? "Polling…" : "Poll now"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="reset-ingestion-btn"
                    disabled={polling || resetting}
                    variant="outline"
                    size="sm"
                    className="rounded-sm gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                  >
                    <ArrowCounterClockwise size={14} className={resetting ? "animate-spin" : ""} />
                    {resetting ? "Resetting…" : "Reset ingestion"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset mailbox ingestion?</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2">
                      <span className="block">
                        This clears the internal tombstone list that remembers which emails have already been processed.
                      </span>
                      <span className="block">
                        The next poll will then re-ingest <strong>every message currently in the shared mailbox</strong> as new leads.
                      </span>
                      <span className="block text-amber-700">
                        Use this only after intentionally deleting all leads for a fresh start.
                        It does <strong>not</strong> delete any existing leads.
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="reset-ingestion-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="reset-ingestion-confirm"
                      onClick={onResetIngestion}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Yes, reset & re-ingest
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
        <div>
          <div className="label-tracking">Azure AD App Registration</div>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            Required permissions: <span className="font-mono text-slate-700">Mail.Read</span>,{" "}
            <span className="font-mono text-slate-700">User.Read.All</span>,{" "}
            <span className="font-mono text-slate-700">Mail.Send</span> (Application). Add{" "}
            <span className="font-mono text-slate-700">Mail.Send.Shared</span> if you send from a shared mailbox.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-tracking">Tenant ID</Label>
            <Input data-testid="tenant-id-input" value={form.azure_tenant_id} onChange={(e) => setForm({ ...form, azure_tenant_id: e.target.value })} className="mt-2 rounded-sm h-10 font-mono text-xs" />
          </div>
          <div>
            <Label className="label-tracking">Client ID</Label>
            <Input data-testid="client-id-input" value={form.azure_client_id} onChange={(e) => setForm({ ...form, azure_client_id: e.target.value })} className="mt-2 rounded-sm h-10 font-mono text-xs" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tracking">Client Secret</Label>
            {settings.azure_client_secret_masked && !replacingSecret ? (
              <div
                data-testid="client-secret-stored"
                className="mt-2 flex items-center gap-2 flex-wrap rounded-sm border border-emerald-200 bg-emerald-50 px-3 h-10"
              >
                <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                <span className="text-xs text-emerald-800">
                  Secret stored: <span className="font-mono">{settings.azure_client_secret_masked}</span>
                </span>
                <button
                  type="button"
                  data-testid="client-secret-replace-btn"
                  onClick={() => setReplacingSecret(true)}
                  className="ml-auto text-[11px] underline text-emerald-700 hover:text-emerald-900"
                >
                  Replace secret
                </button>
              </div>
            ) : (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  data-testid="client-secret-input"
                  type="password"
                  value={form.azure_client_secret}
                  onChange={(e) => setForm({ ...form, azure_client_secret: e.target.value })}
                  placeholder={settings.azure_client_secret_masked ? "Paste new secret value" : "Paste secret value"}
                  className="rounded-sm h-10 font-mono text-xs"
                  autoFocus={!!settings.azure_client_secret_masked}
                />
                {settings.azure_client_secret_masked && (
                  <button
                    type="button"
                    data-testid="client-secret-cancel-btn"
                    onClick={() => { setReplacingSecret(false); setForm({ ...form, azure_client_secret: "" }); }}
                    className="text-[11px] underline text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-1">
              For security we never re-display the secret. Saving without entering a new value keeps the stored one intact.
            </p>
          </div>
          <div>
            <Label className="label-tracking">Shared Mailbox</Label>
            <Input data-testid="shared-mailbox-input" value={form.shared_mailbox} onChange={(e) => setForm({ ...form, shared_mailbox: e.target.value })} className="mt-2 rounded-sm h-10 font-mono text-xs" />
          </div>
          <div>
            <Label className="label-tracking">Allowed Domain</Label>
            <Input data-testid="allowed-domain-input" value={form.allowed_domain} onChange={(e) => setForm({ ...form, allowed_domain: e.target.value })} className="mt-2 rounded-sm h-10 font-mono text-xs" />
          </div>
          <div>
            <Label className="label-tracking">Poll Interval (minutes)</Label>
            <Input data-testid="poll-interval-input" type="number" min={1} value={form.poll_interval_minutes} onChange={(e) => setForm({ ...form, poll_interval_minutes: e.target.value })} className="mt-2 rounded-sm h-10" />
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100">
          <Button data-testid="save-settings-btn" onClick={onSave} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">
            <FloppyDisk size={14} /> {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </section>
    </div>
  );
}
